/**
 * 48 半小时色块拖拽选择器（华为商店红线规则 · 投放时间专用）。
 *
 * 数据契约：ope_value = {"startHMs":"09:00","endHMs":"24:00"}（单段起止），
 * 故选择语义为「单段连续区间」——按下色块为锚点、拖至另一块实时预览、松开提交
 * [min,max] 区间；再次拖拽替换原区间（多段无法用该 JSON 格式表达）。
 * 换算：块 i 覆盖 [i*30, (i+1)*30) 分钟；载入取所有与 [start,end] 相交的块，
 * 保存 startHMs=首块起点、endHMs=末块终点（末块 47 → "24:00"），对齐数据往返无损。
 *
 * 交互实现：容器 pointer 事件 + setPointerCapture；索引按 clientX 相对容器宽度
 * 均分换算（色块单元 flex-1 等宽、间隙收在单元内边距，几何与换算严格一致）。
 * 刻度行：与色块行同为 48 等宽单元（几何一一对齐）；偶数整点 0/2/…/22 的数字
 * 置于该整点起始色块的同列居中（正下方），「24」为终点标记右对齐贴齐最右色块右缘。
 */

import { useMemo, useRef, useState } from "react";

/** 全天 48 个半小时块。 */
const BLOCK_COUNT = 48;
/** 每块分钟数。 */
const MINUTES_PER_BLOCK = 30;

/** 选中块区间（含端点，from <= to）。 */
interface BlockRange {
  from: number;
  to: number;
}

/** 编辑器 props：value 为 ope_value JSON 串，onChange 回传序列化后的新串。 */
interface HalfHourBlocksPickerProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

/** "HH:mm" → 当日分钟数（"24:00" → 1440）；非法返回 null。 */
function parseHm(text: unknown): number | null {
  if (typeof text !== "string") return null;
  const parts = text.split(":");
  if (parts.length !== 2 || !/^\d{1,2}$/.test(parts[0]) || !/^\d{2}$/.test(parts[1])) {
    return null;
  }
  const hour = Number(parts[0]);
  const minute = Number(parts[1]);
  if (minute > 59 || hour > 24) return null;
  if (hour === 24 && minute !== 0) return null;
  return hour * 60 + minute;
}

/** 当日分钟数 → "HH:mm"（1440 → "24:00"）。 */
function fmtHm(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** ope_value JSON → 选中块区间；空区间（start==end）或非法 JSON 返回 null。 */
function parseRange(value: string): BlockRange | null {
  try {
    const obj: unknown = JSON.parse(value);
    if (obj === null || typeof obj !== "object") return null;
    const start = parseHm((obj as Record<string, unknown>).startHMs);
    const end = parseHm((obj as Record<string, unknown>).endHMs);
    if (start === null || end === null || end <= start) return null;
    const from = Math.floor(start / MINUTES_PER_BLOCK);
    const to = Math.min(BLOCK_COUNT - 1, Math.ceil(end / MINUTES_PER_BLOCK) - 1);
    if (to < from) return null;
    return { from, to };
  } catch {
    return null;
  }
}

/** 选中块区间 → ope_value JSON（未选中等价 {"startHMs":"00:00","endHMs":"00:00"}）。 */
function serializeRange(range: BlockRange | null): string {
  if (range === null) {
    return '{"startHMs":"00:00","endHMs":"00:00"}';
  }
  return `{"startHMs":"${fmtHm(range.from * MINUTES_PER_BLOCK)}","endHMs":"${fmtHm(
    (range.to + 1) * MINUTES_PER_BLOCK,
  )}"}`;
}

/** 区间 → 展示文本（如 "09:00 – 24:00"；null → 未选择）。 */
function rangeText(range: BlockRange | null): string {
  if (range === null) return "未选择";
  return `${fmtHm(range.from * MINUTES_PER_BLOCK)} – ${fmtHm(
    (range.to + 1) * MINUTES_PER_BLOCK,
  )}`;
}

export function HalfHourBlocksPicker({
  value,
  onChange,
  disabled = false,
}: HalfHourBlocksPickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  /** 拖拽中状态：anchor=按下锚点块，current=当前指针块；松开时提交 [min,max]。 */
  const [drag, setDrag] = useState<{ anchor: number; current: number } | null>(null);

  const committed = useMemo(() => parseRange(value), [value]);
  /** 展示区间：拖拽中用实时预览，否则用已提交值。 */
  const shown: BlockRange | null = drag
    ? { from: Math.min(drag.anchor, drag.current), to: Math.max(drag.anchor, drag.current) }
    : committed;

  /** 指针事件 → 色块索引（容器等宽 48 单元，clamp 到 [0,47]）。 */
  function blockIndexFromEvent(e: React.PointerEvent<HTMLDivElement>): number {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 0;
    const x = e.clientX - rect.left;
    const idx = Math.floor(x / (rect.width / BLOCK_COUNT));
    return Math.min(BLOCK_COUNT - 1, Math.max(0, idx));
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const idx = blockIndexFromEvent(e);
    setDrag({ anchor: idx, current: idx });
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!drag) return;
    const idx = blockIndexFromEvent(e);
    if (idx !== drag.current) setDrag({ anchor: drag.anchor, current: idx });
  }

  function onPointerUp() {
    if (!drag) return;
    const from = Math.min(drag.anchor, drag.current);
    const to = Math.max(drag.anchor, drag.current);
    setDrag(null);
    onChange(serializeRange({ from, to }));
  }

  function onPointerCancel() {
    setDrag(null);
  }

  return (
    <div className="w-full">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-[11px] text-text-muted-weak">
          拖拽选择连续投放时段（30 分钟粒度，再次拖拽替换原区间）
        </span>
        <span className="font-mono text-[12px] text-text-strong">{rangeText(shown)}</span>
      </div>
      <div
        ref={containerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        className={
          disabled
            ? "flex w-full cursor-default select-none"
            : "flex w-full cursor-pointer touch-none select-none"
        }
      >
        {Array.from({ length: BLOCK_COUNT }, (_, i) => (
          <div key={i} className="min-w-0 flex-1 px-[1px] py-[2px]">
            <div
              className={
                shown && i >= shown.from && i <= shown.to
                  ? "h-5 w-full rounded-[2px] bg-[#3b82f6]"
                  : "h-5 w-full rounded-[2px] bg-slate-300 dark:bg-slate-600"
              }
            />
          </div>
        ))}
      </div>
      {/* 刻度行：48 个单元与上方色块行一一同宽同列（min-w-0 + flex-1 均分），
          保证每个小时数字严格落在对应色块正下方：块 i 的起点为 i/2 点整，
          i%4===0 即 0/2/…/22 点，数字在该块同列居中；最末单元右对齐「24」
          贴齐最右色块右缘，作为 24:00 终点标记。 */}
      <div className="mt-1 flex select-none text-[10px] text-text-muted-weak">
        {Array.from({ length: BLOCK_COUNT }, (_, i) => (
          <div
            key={i}
            className={`min-w-0 flex-1 whitespace-nowrap ${
              i === BLOCK_COUNT - 1 ? "text-right" : "text-center"
            }`}
          >
            {i % 4 === 0 ? i / 2 : i === BLOCK_COUNT - 1 ? 24 : ""}
          </div>
        ))}
      </div>
    </div>
  );
}
